import { Component, input, InputSignal } from '@angular/core';
import { DialogComponent } from '@app/components';
import { ButtonComponent, DialogFooter } from '@app/components/platform';
import { isKYBSigningOfficerRequiredError } from '@app/errors/kyb/kyb-signing-officer-required.error';
import { NotifyService } from '@app/services';
import { KYCKYBService } from '@app/services/kyc-kyb/kyc-kyb.service';
import { TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import { KYBSigningOfficerForm } from '../kyb-signing-officer-form/kyb-signing-officer-form';
import { type PersonaScope } from '../types';

@Component({
    templateUrl: 'kyc-kyb-instructions.template.html',
    selector: 'app-kyc-kyb-instructions',
    standalone: true,
    imports: [DialogComponent, ButtonComponent, DialogFooter, TranslateModule, KYBSigningOfficerForm],
    styleUrls: ['./kyc-kyb-instructions.style.scss'],
})
export class KYCKYBInstructions {
    scope: InputSignal<PersonaScope> = input.required();

    isKYBSigningOfficerFormOpen = false;

    constructor(
        public knowYourService: KYCKYBService,
        private notifyService: NotifyService
    ) {}

    closeDialog(): void {
        this.isKYBSigningOfficerFormOpen = false;
    }

    launchPersona(): void {
        this.knowYourService.launchPersona(this.scope()).subscribe({
            error: (error) => {
                // The KYBSigningOfficerRequiredError is an expected error if we're launching KYB for the first time
                // since the dialog hasn't been shown to collect signing officer details yet.
                // When that error is thrown we simply show the dialog, if any other error is thrown then we need to follow typical error flow.
                if (!isKYBSigningOfficerRequiredError(error)) {
                    this.handlePersonaLaunchError(error);
                    return;
                }
                this.isKYBSigningOfficerFormOpen = true;
            },
        });
    }

    submitSigningOfficerForm(form: KYBSigningOfficerForm): void {
        const fields = form.onSubmit();

        // If the form wasn't valid, no data is returned
        if (!fields) {
            return;
        }

        this.isKYBSigningOfficerFormOpen = false;

        // Actually launch Persona now that we have singing officer details
        this.knowYourService.launchPersona(this.scope(), fields).subscribe({
            error: this.handlePersonaLaunchError,
        });
    }

    handlePersonaLaunchError(error: Error): void {
        this.notifyService.error('payroll.kyc-kyb.pre-redirect.error');
        Sentry.captureException(error);
    }
}
